/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ISchemaResponsiveValueModel,
	SCHEMA_VALUE_TYPE,
	TGenericBlueprintSchema,
	TGenericModelNode
} from "@adapptio/lib-blueprint";
import React, { useCallback } from "react";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { FieldHeaderButton } from "../../Fields/FieldHeaderButton";
import { InspectorControl } from "../InspectorControl";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { getInspectorControls } from "./inspectorControls";
import { isMediaResolutionId, usePreviewResolutionContext } from "../../../../previewResolution";

interface IControlResponsiveValue_EditorMetaData {
	showExpanded?: boolean;
}

export const ControlResponsiveValue = (_model: TGenericModelNode): IInspectorControls => {
	const model = _model as ISchemaResponsiveValueModel<TGenericBlueprintSchema>;
	const meta = model.schema.getEditorMetaData<IControlResponsiveValue_EditorMetaData>(model);

	const { previewResolutionId } = usePreviewResolutionContext() || {};

	if (model.value?.type === SCHEMA_VALUE_TYPE.CONST && meta.showExpanded) {
		return {
			...getInspectorControls(model.value.constant),
			headerControls: {
				component: ControlResponsiveValue_HeaderControls,
				model: model
			}
		};
	} else if (model.value?.type === SCHEMA_VALUE_TYPE.CONST) {
		const defaultId = "screenBase";
		const mainControlId = isMediaResolutionId(previewResolutionId) ? previewResolutionId : defaultId;
		const controlModel =
			model.value.constant.props[mainControlId] || model.value.constant.props[defaultId];
		const propControls = getInspectorControls(controlModel);

		return {
			...propControls,
			headerControls: {
				component: ControlResponsiveValue_HeaderControls,
				model: model
			},
			dependencyModels: [ controlModel, ...(propControls.dependencyModels || []) ]
		};
	} else {
		return getInspectorControls(model.value);
	}
};

export const ControlResponsiveValue_HeaderControls: TInspectorControlComponent<
	ISchemaResponsiveValueModel<TGenericBlueprintSchema>
> = ({ model: _model, path, depth }) => {
	const { model } = useBlueprintModel(_model, "responsiveValue");

	const toggleShowExpanded = useCallback(() => {
		if (!model) {
			return;
		}

		const prevMeta = model.schema.getEditorMetaData<IControlResponsiveValue_EditorMetaData>(model);
		model.schema.setEditorMetaData<IControlResponsiveValue_EditorMetaData>(model, {
			showExpanded: !prevMeta.showExpanded
		});
	}, [ model ]);

	const meta = model.schema.getEditorMetaData<IControlResponsiveValue_EditorMetaData>(model);

	let propControls: IInspectorControls;

	if (model.value?.type === SCHEMA_VALUE_TYPE.CONST && meta.showExpanded) {
		propControls = getInspectorControls(model.value.constant);
	} else {
		// @todo change prop based on resolution
		propControls = getInspectorControls(model.value.constant.props.screenBase);
	}

	return (
		<>
			<FieldHeaderButton
				icon="mdi/responsive"
				isActive={meta.showExpanded}
				onClick={toggleShowExpanded}
			/>
			{propControls.headerControls ? (
				<InspectorControl config={propControls.headerControls} path={path} depth={depth} />
			) : null}
		</>
	);
};
